import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableEnergyPointReport,
  RequestReportInterFace,
} from '../../../../interface/reportenergyPoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchEnergyReportPoint,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setEnergyPointReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportEnergyPointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../../shared-functions/DataTime';
import { getDaysBetween } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * EnergyReportTablePoint component
 * @param {ReportTableProperties} properties - The properties
 * @returns {React.ReactElement} The rendered component
 */
function EnergyReportTablePoint(properties: Readonly<ReportTableProperties>) {
  const { t } = useTranslation('report');
  const branding = useAppBranding();
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const previousAccountId = useRef<string | undefined>(undefined);

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });
  const previousDateRange = useRef<{ startDate: string; endDate: string }>({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isEnergyPointReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportEnergyPoint);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  /**
   * Fetches energy report data for the selected facility.
   * @param {RequestReportInterFace} data - The request parameters for fetching the report.
   */
  const fetchData = useCallback(
    (data: RequestReportInterFace) => {
      if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
        dispatch(
          setNotificationMessage({
            message: t('dateRangeLimitError'),
            status: true,
            type: 'error',
            code: 400,
          }),
        );
        return;
      }
      dispatch(
        fetchEnergyReportPoint({
          ...data,
          accountCustomId: data.accountCustomId || reportsData?.accountId,
        }),
      );
    },
    [dispatch, reportsData?.accountId, t],
  );

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };
  const {
    isEditMode,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleOpenStationSidePanel,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: reports,
    disableEdit: true,
    disableDelete: true,
  });
  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableEnergyPointReport,
      /**
       * S.No view
       * @param {AvailableEnergyPointReport} _report - The report
       * @param {number} index - The index
       * @returns {React.ReactElement} The rendered element
       */
      customData: (_report: AvailableEnergyPointReport, index: number) => (
        <div className="table-row-each-data align-center">{(page - 1) * limit + index + 1}</div>
      ),
      columnWidth: 50,
    },
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailableEnergyPointReport,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableEnergyPointReport,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
      /**
       * Station info view
       * @param {AvailableEnergyPointReport} report - The report data
       * @returns {React.ReactElement} The rendered element
       */
      customData: (report: AvailableEnergyPointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenStationSidePanel(report);
              }}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="presentation">{report.chargingStationLocation}</span>
          </div>
        </div>
      ),
    },

    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * Decimal value round off
       * @param {AvailableEnergyPointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.powerConsumed, 3)} kWh</span>
      ),
    },
    {
      label: t('revenue'),
      accessorKey: 'totalRevenue' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * Decimal value round off
       * @param {AvailableEnergyPointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('sessionCount'),
      accessorKey: 'sessionCount' as keyof AvailableEnergyPointReport,
      sortAllow: true,
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * Formatted duration
       * @param {AvailableEnergyPointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{FormatDuration(row?.durationSeconds, { formatBy: 'sec' })}</span>
      ),
    },
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      accessorKey: 'createdAt' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Created at date formatter
       * @param {AvailableEnergyPointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>
          {ConvertUTCtoDateTime(
            row?.createdAt || (row as any)?.created || (row as any)?.created_at || '',
          ) || '-'}
        </span>
      ),
    },
    {
      label: t('lastUpdated', { defaultValue: 'Last Updated' }),
      accessorKey: 'updatedAt' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Updated at date formatter
       * @param {AvailableEnergyPointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>
          {ConvertUTCtoDateTime(
            (row as any)?.updatedAt || (row as any)?.lastUpdated || (row as any)?.updated_at || '',
          ) || '-'}
        </span>
      ),
    },
  ];

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit,
    });
  };

  /**
   * Current columns for Energy Point Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('chargingPoint'), accessorKey: 'chargingPointSn' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('revenue'), accessorKey: 'totalRevenue' },
    { label: t('sessionCount'), accessorKey: 'sessionCount' },
    { label: t('duration'), accessorKey: 'durationSeconds' },
    { label: t('createdAt', { defaultValue: 'Created At' }), accessorKey: 'createdAt' },
    { label: t('lastUpdated', { defaultValue: 'Last Updated' }), accessorKey: 'updatedAt' },
  ];
  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });
  /**
   * Export data function
   * @param {string} type - The type ('csv' | 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchEnergicPointListExport(
        removeEmptyKeys({
          ...exportParameters,
          accountCustomId: reportsData?.accountId,
        }),
      )
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableEnergyPointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'chargingPointSn':
                return row?.chargingPointSn || '-';

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'powerConsumed':
                return row?.powerConsumed ? ToFixedValue(row.powerConsumed, 3) : '0.000';

              case 'totalRevenue':
                return row?.totalRevenue
                  ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'sessionCount':
                return row?.sessionCount ?? '0';

              case 'durationSeconds':
                return row?.durationSeconds
                  ? FormatDuration(Number(row.durationSeconds), { formatBy: 'sec' })
                  : '-';

              case 'createdAt':
                return (row as any)?.createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any)?.updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortField,
      sortBy,
    });
  };

  /**
   * Fetches energy report data with query parameters.
   * @param {string} [customAccountId] - The account custom ID.
   * @param {boolean} [resetFilters] - Whether to reset filters.
   */
  const fetchReportData = useCallback(
    (customAccountId?: string, resetFilters = false) => {
      const accountCustomId = customAccountId || reportsData?.accountId;
      const queryParameters = resetFilters ? {} : GetParametersFromCommonFilters(filters);
      if (accountCustomId) {
        fetchData({
          ...queryParameters,
          accountCustomId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          page: DefaultApiLimit.page,
          limit: DefaultApiLimit.limit,
        });
      }
    },
    [filters, dateRange.startDate, dateRange.endDate, reportsData?.accountId, fetchData],
  );

  useEffect(() => {
    const isDateRangeChanged =
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate;

    if (
      isFilterUpdated ||
      isEnergyPointReportUpdate ||
      reportsData.isReportUpdated ||
      isDateRangeChanged
    ) {
      if (isDateRangeChanged) {
        previousDateRange.current = dateRange;
      }
      fetchReportData();
      if (reportsData.isReportUpdated) {
        dispatch(resetReportUpdateFlag());
      }
      if (isEnergyPointReportUpdate) {
        dispatch(setEnergyPointReportUpdate(false));
      }
    }
  }, [
    fetchReportData,
    isFilterUpdated,
    isEnergyPointReportUpdate,
    reportsData.isReportUpdated,
    dateRange.startDate,
    dateRange.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'energyReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (reportsData?.accountId) {
      if (previousAccountId.current !== reportsData.accountId) {
        previousAccountId.current = reportsData.accountId;
        dispatch(fetchListFilters({ accountCustomId: reportsData.accountId }));
        fetchReportData(reportsData.accountId, true);
      }
    }
  }, [reportsData?.accountId, fetchReportData, dispatch]);

  return (
    <div>
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={handleApiRequest}
        version="v2"
        exportDataPdfOrCSV={
          permissionCheck('mobilityEnergyReportExport') ? exportDataPdfOrCSV : undefined
        }
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        filters={filters}
        height={370}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        refershCurrentPages={ReferenceCurrentPages}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        sizeChangeButtonRequired
        onHiddenColumnsChange={setHiddenColumns}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        rightTopContent={
          <div className="table-bottom-button-list">
            {renderDateFilters()}
            <div className="right-action">{rightTopContent}</div>
          </div>
        }
        onClearFilterChange={() => {
          clearDateRange();
        }}
      />
    </div>
  );
}

export default EnergyReportTablePoint;
